export class ProductInfo extends HTMLElement {
  abortController = undefined;
  swiper = undefined;

  constructor() {
    super();
  }

  setupEventListeners() {
    // Delegated rather than bound to each <variant-selector>. The variant-picker block
    // is replaced wholesale on every variant change, and <variant-selector> is a plain
    // markup hook, not a registered custom element, so a directly bound listener would
    // be destroyed by the first update and never re-attached.
    this.addEventListener('change', this.onVariantChange.bind(this));
    this.addEventListener('change', this.onQuantitySelectorEvent.bind(this));
    this.addEventListener('click', this.onQuantitySelectorEvent.bind(this));
    this.addEventListener('click', this.onMediaGridToggle.bind(this));
  }

  connectedCallback() {
    this.setupEventListeners();
    this.initSwiper();
    this.setupStickyAtc();
    this.setupGalleryVideos();
  }

  disconnectedCallback() {
    this.stickyAtcObserver?.disconnect();
    this.galleryVideoObserver?.disconnect();
    this.stickyAtcResizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--product-sticky-atc-height');
  }

  initSwiper() {
    // Every gallery Swiper is tracked so updateGallery can tear them down before swapping the markup.
    this.gallerySwipers = [];

    const thumbnailGalleryEl = this.querySelector('.product-media-gallery__thumbnails');
    const thumbnailWrapperEl = this.querySelector('.product-media-gallery__thumbnails-wrapper');
    if (thumbnailGalleryEl) {
      const mainGalleryEl = this.querySelector('.product-media-gallery__main');
      if (mainGalleryEl) {
        const thumbsSwiper = new Swiper(thumbnailGalleryEl, {
          spaceBetween: 9.5,
          slidesPerView: 'auto',
          freeMode: true,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          navigation: {
            prevEl: thumbnailWrapperEl?.querySelector('[data-swiper-prev]') || thumbnailGalleryEl.querySelector('[data-swiper-prev]'),
            nextEl: thumbnailWrapperEl?.querySelector('[data-swiper-next]') || thumbnailGalleryEl.querySelector('[data-swiper-next]'),
          },
          breakpoints: {
            768: {
              spaceBetween: 14,
            },
            1024: {
              spaceBetween: 16,
            },
          },
        });
        this.swiper = new Swiper(mainGalleryEl, {
          spaceBetween: 0,
          thumbs: {
            swiper: thumbsSwiper,
          },
        });
        this.gallerySwipers.push(thumbsSwiper, this.swiper);
      }
    }

    const carouselGalleryEl = this.querySelector('.product-media-gallery__carousel');
    if (carouselGalleryEl) {
      this.swiper = new Swiper(carouselGalleryEl, {
        autoHeight: true,
        direction: 'horizontal',
        pagination: {
          el: carouselGalleryEl.querySelector('.swiper-pagination'),
        },
        navigation: {
          prevEl: carouselGalleryEl.querySelector('[data-swiper-prev]'),
          nextEl: carouselGalleryEl.querySelector('[data-swiper-next]'),
        },
      });
      this.gallerySwipers.push(this.swiper);
    }
  }

  /**
   * Plays the gallery video that is mostly in view and pauses the others. Swiper slides and "Show more"
   * tiles outside the visible area never intersect, so swiping or collapsing pauses them too. Videos are
   * muted with visible controls (WCAG 2.2.2), and nothing autoplays for reduced-motion users.
   */
  setupGalleryVideos() {
    this.galleryVideoObserver?.disconnect();

    const videos = this.querySelectorAll('media-gallery video');
    if (!videos.length || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.galleryVideoObserver = new IntersectionObserver(
      (entries) => {
        for (const { target, isIntersecting } of entries) {
          if (isIntersecting) {
            target.play().catch(() => {});
          } else {
            target.pause();
          }
        }
      },
      { threshold: 0.6 }
    );

    for (const video of videos) {
      this.galleryVideoObserver.observe(video);
    }
  }

  /**
   * Replaces the gallery and zoom modal media when the variant media set changes. Returns false when
   * the set is unchanged, including both variants falling back to product media (no data-media-set),
   * so the caller keeps the slide/reorder behaviour.
   * @param {Document} html - Parsed markup from the section rendering API
   * @returns {boolean} Whether the gallery was replaced
   */
  updateGallery(html) {
    const gallery = this.querySelector('media-gallery');
    const nextGallery = html.querySelector('media-gallery');
    if (!gallery || !nextGallery) return false;
    if (gallery.dataset.mediaSet === nextGallery.dataset.mediaSet) return false;

    const hadFocus = gallery.contains(document.activeElement);

    for (const swiper of this.gallerySwipers || []) {
      swiper.destroy(true, true);
    }
    this.galleryVideoObserver?.disconnect();

    gallery.replaceWith(nextGallery);

    const modalContent = this.querySelector('[data-modal-content]');
    const nextModalContent = html.querySelector('[data-modal-content]');
    if (modalContent && nextModalContent) {
      modalContent.replaceChildren(...nextModalContent.children);
    }

    this.initSwiper();
    this.setupGalleryVideos();

    // Focus was inside media that no longer exists; keep it in the gallery rather than on <body>.
    if (hadFocus) {
      nextGallery.setAttribute('tabindex', '-1');
      nextGallery.focus({ preventScroll: true });
    }

    return true;
  }

  get variantSelector() {
    return this.querySelector('variant-selector');
  }

  get variantSelectors() {
    return Array.from(this.querySelectorAll('variant-selector'));
  }

  get quantitySelector() {
    return this.querySelector('quantity-selector');
  }

  get quantitySelectors() {
    return Array.from(this.querySelectorAll('quantity-selector'));
  }

  /**
   * Mobile sticky add-to-cart bar: shows once the main buy button scrolls out
   * of view. Re-run after variant-change swaps the buy-buttons block, since
   * that replaces both the bar and the button it observes with fresh nodes.
   */
  setupStickyAtc() {
    this.stickyAtcObserver?.disconnect();
    this.stickyAtcResizeObserver?.disconnect();

    const stickyBar = this.querySelector('.product-sticky-atc');
    const mainButton = Array.from(this.querySelectorAll('.add-to-cart-container'))
      .find((container) => !container.closest('.product-sticky-atc'))
      ?.querySelector('.add-to-cart-button');
    if (!stickyBar || !mainButton) return;

    stickyBar.classList.remove('is-visible');

    if (!('IntersectionObserver' in window)) return;

    this.stickyAtcObserver = new IntersectionObserver(([entry]) => {
      stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
    }, { threshold: 0.01 });
    this.stickyAtcObserver.observe(mainButton);

    if (!('ResizeObserver' in window)) return;

    this.stickyAtcResizeObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--product-sticky-atc-height', `${stickyBar.offsetHeight}px`);
    });
    this.stickyAtcResizeObserver.observe(stickyBar);
  }

  get selectedOptionValues() {
    const list = [];

    this.variantSelectors.forEach((selector) => {
      if (selector.dataset.pickerType === 'dropdown') {
        Array.from(selector.querySelectorAll('select')).forEach((select) => {
          const optionValueId = select.options[select.selectedIndex]?.dataset?.optionValueId;
          if (optionValueId) list.push(optionValueId);
        });
      } else {
        Array.from(selector.querySelectorAll('fieldset input:checked')).forEach(({ dataset }) => {
          if (dataset.optionValueId) list.push(dataset.optionValueId);
        });
      }
    });

    return list;
  }

  getSelectedVariant(html) {
    const selectedVariant = html.querySelector('[data-selected-variant]')?.innerHTML;
    return selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onVariantChange(e) {
    // The change listener is on product-info, so ignore changes from quantity, forms, etc.
    if (!e.target.closest('variant-selector')) {
      return;
    }

    const productUrlChanged = e.target?.dataset?.productUrl ? e.target.dataset.productUrl !== this.dataset.url : false;
    const productUrl = e.target?.dataset?.productUrl || this.dataset.url;
    this.renderSection(productUrlChanged, productUrl);
  }

  get currentVariantId() {
    return (
      this.querySelector('form[id^="product-form"] input[name="id"]')?.value ||
      this.querySelector('product-group-picker')?.dataset.variantId ||
      null
    );
  }

  /**
   * Switch to another colorway product from the group picker.
   * @param {string} productUrl
   * @param {{ variantId?: string }} [options]
   */
  switchToProduct(productUrl, options = {}) {
    if (!productUrl) return;

    const { variantId = null } = options;
    const currentPath = this.dataset.url?.split('?')[0];
    const nextPath = productUrl.split('?')[0];
    const sameProduct = nextPath === currentPath;

    if (sameProduct && !variantId) return;
    if (sameProduct && variantId && String(variantId) === String(this.currentVariantId || '')) return;

    this.classList.add('is-product-switching');
    this.renderSection(!sameProduct, productUrl, {
      skipOptionValues: true,
      variantId,
    });
  }

  onQuantitySelectorEvent(e) {
    // Scoped to whichever <quantity-selector> the event actually came from -
    // the main one or the mobile sticky bar's mirror - rather than always the
    // first, so both instances work independently and stay in sync below.
    const selector = e.target.closest('quantity-selector');
    if (!selector || !this.contains(selector)) return;

    const quantityInput = selector.querySelector('input[type="number"]');
    if (!quantityInput) return;
    const minValue = parseInt(quantityInput.getAttribute('min'), 10) || 0;
    const parsedMax = parseInt(quantityInput.getAttribute('max'), 10);
    const maxValue = Number.isNaN(parsedMax) ? Infinity : parsedMax;

    // Truncate whatever is in the field: a typed "2.7" or "abc" must never reach the cart.
    let value = Math.trunc(Number.parseFloat(quantityInput.value));
    if (Number.isNaN(value)) {
      value = minValue;
    }

    const action = e.target.closest('button')?.name;
    if (action === 'minus') {
      value -= 1;
    } else if (action === 'plus') {
      value += 1;
    }

    const clampedValue = Math.min(Math.max(value, minValue), maxValue);
    quantityInput.value = clampedValue;

    for (const otherSelector of this.quantitySelectors) {
      if (otherSelector === selector) continue;
      const otherInput = otherSelector.querySelector('input[type="number"]');
      if (otherInput) otherInput.value = clampedValue;
    }
  }

  updateMedia(variantFeaturedMediaId) {
    if (!variantFeaturedMediaId) return;
    const mediaSlide = this.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
    if (!mediaSlide) return;

    const slideIndex = parseInt(mediaSlide.dataset.mediaIndex);
    this.swiper?.slideTo(slideIndex);
  }

  /**
   * The 2-columns grid has no slider to move, so reorder its tiles to match the
   * re-rendered section, which puts the variant's featured media first. Existing
   * nodes are moved rather than replaced, so loaded images and zoom handlers survive.
   * @param {Document} html - Parsed markup from the section rendering API
   */
  updateGridMedia(html) {
    const grid = this.querySelector('.media-gallery__2columns');
    const nextGrid = html.querySelector('.media-gallery__2columns');
    if (!grid || !nextGrid) return;

    const itemsById = new Map();
    for (const item of grid.children) {
      itemsById.set(item.dataset.mediaId, item);
    }

    for (const nextItem of nextGrid.children) {
      const item = itemsById.get(nextItem.dataset.mediaId);
      if (item) grid.append(item);
    }
  }

  /**
   * Reveals or re-clips the 2-columns grid tiles past the sixth.
   * @param {MouseEvent} event
   */
  onMediaGridToggle(event) {
    const toggle = event.target.closest('[data-media-grid-toggle]');
    if (!toggle) return;

    const grid = this.querySelector('.media-gallery__2columns');
    if (!grid) return;

    const isExpanded = toggle.getAttribute('aria-expanded') !== 'true';
    grid.classList.toggle('media-gallery__2columns--expanded', isExpanded);
    toggle.setAttribute('aria-expanded', String(isExpanded));

    const label = toggle.querySelector('[data-media-grid-toggle-label]');
    if (label) label.textContent = isExpanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;

    // Collapsing removes the tiles above the toggle, so bring it back into view.
    if (!isExpanded) toggle.scrollIntoView({ block: 'center' });
  }

  updateURL(variantId) {
    // Don't update URL if this is in a modal/quick-add context
    if (this.dataset.updateUrl === 'false') return;

    if (!window.location.pathname.includes('/products/')) return;
    window.history.replaceState({}, '', `${this.dataset.url}${variantId ? `?variant=${variantId}` : ''}`);
  }

  /**
   * Scroll product-info to the top of the viewport after a product-group switch.
   * Skips modal/quick-add contexts. Respects sticky header height + reduced motion.
   */
  scrollIntoViewTop() {
    if (this.dataset.updateUrl === 'false') return;

    requestAnimationFrame(() => {
      const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
      const top = this.getBoundingClientRect().top + window.scrollY - headerOffset;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    });
  }

  /**
   * Copy innerHTML from the re-rendered section into this one, for every element
   * matching the selector. Matches are paired by document order, which holds because
   * both documents are the same section with the same block order.
   * @param {Document} html - Parsed markup from the section rendering API
   * @param {string} selector - CSS selector, queried in both documents
   * @returns {void}
   */
  updateSourceFromDestination = (html, selector) => {
    const sources = html.querySelectorAll(selector);
    const destinations = this.querySelectorAll(selector);
    if (!sources.length || !destinations.length) {
      return;
    }

    for (const [index, destination] of destinations.entries()) {
      const source = sources[index];
      if (!source) {
        break;
      }

      destination.innerHTML = source.innerHTML;
    }
  };

  updateVariantInputs(variantId) {
    this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach(
      (productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = variantId ?? '';
      },
    );
  }

  getDependentSectionIds() {
    return [
      ...new Set(
        Array.from(document.querySelectorAll('[data-product-switch-section]'))
          .map((el) => {
            const shopifySection = el.closest('.shopify-section');
            if (shopifySection?.id?.startsWith('shopify-section-')) {
              return shopifySection.id.replace(/^shopify-section-/, '');
            }
            return el.dataset.section || null;
          })
          .filter(Boolean),
      ),
    ];
  }

  async renderDependentSections(productUrl, signal) {
    if (this.dataset.updateUrl === 'false') return;

    const sectionIds = this.getDependentSectionIds();
    if (!sectionIds.length) return;

    const fetchUrl = new URL(productUrl, window.location.origin);
    fetchUrl.searchParams.set('sections', sectionIds.join(','));

    const response = await fetch(`${fetchUrl.pathname}?${fetchUrl.searchParams.toString()}`, { signal });
    if (!response.ok) return;

    const sections = await response.json();

    sectionIds.forEach((id) => {
      const sectionHtml = sections?.[id];
      if (!sectionHtml) return;

      const current = document.getElementById(`shopify-section-${id}`);
      if (!current) return;

      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const next = doc.getElementById(`shopify-section-${id}`) || doc.body.firstElementChild;
      if (!next) return;

      current.replaceWith(next);
      this.hydrateSectionScripts(next);
    });
  }

  /**
   * DOMParser-inserted <script> tags do not execute — recreate them so
   * section modules (video-banner, slideshow-list, etc.) load when needed.
   * With includeInline, inline scripts run too: third-party widgets such as
   * Locally boot from an inline config script and stay empty otherwise.
   * JSON data scripts are left alone.
   * @param {Element} root
   * @param {{ includeInline?: boolean }} [options]
   */
  hydrateSectionScripts(root, { includeInline = false } = {}) {
    if (!root) return;

    const selector = includeInline ? 'script' : 'script[src]';
    for (const oldScript of root.querySelectorAll(selector)) {
      if (!oldScript.src && !ProductInfo.isExecutableScript(oldScript)) continue;

      const script = document.createElement('script');
      for (const attr of oldScript.attributes) script.setAttribute(attr.name, attr.value);

      // A classic inline script that ran on page load already declared its top-level const/let
      // globally (Wonderment's ETA block declares queryParams, API_BASE_URL...), so running it again
      // throws "already declared" and stops. A block gives them a fresh scope; var stays global.
      // Empty scripts stay empty: Locally's #lcly-script-0 is an empty placeholder whose src its
      // config sets afterwards, and a script with any content counts as already run and ignores src.
      const isClassicInline =
        !oldScript.src && oldScript.textContent.trim() !== '' && script.type.trim().toLowerCase() !== 'module';
      script.textContent = isClassicInline ? `{\n${oldScript.textContent}\n}` : oldScript.textContent;
      oldScript.replaceWith(script);
    }
  }

  /**
   * @param {HTMLScriptElement} script
   * @returns {boolean} Whether the script's type is JavaScript rather than data (JSON, ld+json)
   */
  static isExecutableScript(script) {
    const type = script.type.trim().toLowerCase();
    return !type || type === 'module' || type === 'text/javascript' || type === 'application/javascript';
  }

  renderSection(productUrlChanged, productUrl, options = {}) {
    const { skipOptionValues = false, variantId = null } = options;

    this.abortController?.abort();
    this.abortController = new AbortController();

    // If the section is in a modal, use the original section id without the modal suffix
    const sectionId = this.dataset.updateUrl === 'false' ? this.dataset.section.split('-modal')[0] : this.dataset.section;

    const fetchUrl = new URL(productUrl, window.location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);
    if (variantId) {
      fetchUrl.searchParams.set('variant', variantId);
    }
    if (!skipOptionValues) {
      const optionValues = this.selectedOptionValues;
      if (optionValues.length) {
        fetchUrl.searchParams.set('option_values', optionValues.join(','));
      }
    }

    fetch(`${fetchUrl.pathname}?${fetchUrl.searchParams.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        // If the section is in a modal, replace the original section id with the modal section id
        if (this.dataset.updateUrl === 'false') {
          responseText = responseText.replaceAll(this.dataset.section.split('-modal')[0], this.dataset.section);
        }
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const variant = this.getSelectedVariant(html);
        if (productUrlChanged) {
          // Breadcrumb is a sibling of product-info in the section, not a child.
          const nextBreadcrumb = html.querySelector('.breadcrumb--product');
          const currentBreadcrumb =
            this.closest('.shopify-section')?.querySelector('.breadcrumb--product') || document.querySelector('.breadcrumb--product');
          if (nextBreadcrumb && currentBreadcrumb) {
            currentBreadcrumb.replaceWith(nextBreadcrumb);
          }

          const productInfo = html.querySelector('product-info');
          const signal = this.abortController.signal;
          this.replaceWith(productInfo);
          // Re-run the new buy box's scripts so third-party widgets (Locally,
          // Wonderment) mount again instead of staying empty after the swap.
          productInfo.hydrateSectionScripts(productInfo, { includeInline: true });
          productInfo.updateURL(variant?.id);
          productInfo.scrollIntoViewTop();

          productInfo.renderDependentSections(productUrl, signal).catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
        } else {
          if (!this.updateGallery(html)) {
            this.updateMedia(variant?.featured_media?.id);
            this.updateGridMedia(html);
          }
          this.updateURL(variant?.id);
          this.updateVariantInputs(variant?.id);
          // this.updateSourceFromDestination(html, `add-to-cart-container-${this.dataset.section}`);
          this.updateSourceFromDestination(html, '.product__info-block-item[data-block-type="buy-buttons"]');
          this.updateSourceFromDestination(html, '.product__info-block-item[data-block-type="variant-picker"]');
          this.updateSourceFromDestination(html, '.product__info-block-item[data-block-type="price"]');
          this.updateSourceFromDestination(html, '.product__info-block-item[data-block-type="sku"]');
          this.updateSourceFromDestination(html, '.product__info-block-item[data-block-type="inventory"]');
          this.updateSourceFromDestination(html, '.product__info-block-item[data-block-type="back-in-stock"]');
          this.updateSourceFromDestination(html, '.product__info-block-item[data-block-type="callout"]');
          this.updateSourceFromDestination(html, '.product__info-block-item[data-block-type="custom-button-sold-out"]');
          this.setupStickyAtc();
          this.classList.remove('is-product-switching');

          const groupPicker = this.querySelector('product-group-picker');
          if (groupPicker?.connectedCallback) {
            groupPicker.disconnectedCallback?.();
            groupPicker.connectedCallback();
          }
        }
      })
      .catch((error) => {
        this.classList.remove('is-product-switching');
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}
